// REST + SSE client for the admin AI agent (PLAN 2 backend).

import api from './agentApiClient';
import { createSSEParser } from './sseParser';
import * as sessionStore from './sessionStore';
import type {
  AgentConversationSummary,
  AgentConversationDetail,
  AgentProposal,
  AgentSSEEvent,
  ChatRequest,
} from '../types/agent';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// The proposal `endpoint` is an absolute admin path (/api/v1/admin/...). The
// axios instance already prefixes /api/v1, so strip it for the relative call.
function toRelativeAdminPath(endpoint: string): string {
  return endpoint.startsWith('/api/v1') ? endpoint.slice('/api/v1'.length) : endpoint;
}

export const agentAPI = {
  async listConversations(): Promise<AgentConversationSummary[]> {
    const response = await api.get('/admin/agent/conversations');
    return response.data;
  },

  async getConversation(id: number): Promise<AgentConversationDetail> {
    const response = await api.get(`/admin/agent/conversations/${id}`);
    return response.data;
  },

  async renameConversation(
    id: number,
    title: string,
  ): Promise<AgentConversationSummary> {
    const response = await api.patch(`/admin/agent/conversations/${id}`, { title });
    return response.data;
  },

  async deleteConversation(id: number): Promise<void> {
    await api.delete(`/admin/agent/conversations/${id}`);
  },

  // Execute a confirmed proposal against the EXISTING admin REST endpoint.
  async executeProposal(proposal: AgentProposal): Promise<any> {
    const path = toRelativeAdminPath(proposal.endpoint);
    switch (proposal.method) {
      case 'POST':
        return (await api.post(path, proposal.payload)).data;
      case 'PUT':
        return (await api.put(path, proposal.payload)).data;
      case 'PATCH':
        return (await api.patch(path, proposal.payload)).data;
      case 'DELETE':
        return (await api.delete(path, { data: proposal.payload })).data;
      default:
        throw new Error(`Unsupported proposal method: ${proposal.method}`);
    }
  },

  // Stream the chat over SSE. Uses fetch (EventSource cannot set the Bearer
  // header this app relies on). Calls onEvent for each decoded SSE event.
  async streamChat(
    request: ChatRequest,
    onEvent: (event: AgentSSEEvent) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const token = sessionStore.getActiveAccessToken();
    const response = await fetch(`${API_BASE_URL}/admin/agent/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(request),
      credentials: 'include',
      signal,
    });

    if (!response.ok || !response.body) {
      onEvent({ type: 'error', message: 'асистент тимчасово недоступний' });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parser = createSSEParser();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      for (const event of parser.push(chunk)) {
        onEvent(event);
      }
    }
  },
};

export default agentAPI;
